import type { Signal } from '../../../../shared/types';
import { shell } from '../../app';
import { copyText } from '../../data/drafts';
import { signalAddress, signalSource } from '../../logic/signals';
import { modal } from '../../ui/dialog';
import type { Modal } from '../../ui/dialog';
import { h } from '../../ui/dom';
import type { Triage } from './inboxTriage';

/** The … sheet of a card (D74): what a business is not, or when to see it again. Each choice closes the
 *  sheet first. The rules themselves live on the server (D72); this only names the reason. */

export interface NotFitActions {
  chain: () => void;
  kind: () => void;
  area: () => void;
  later: () => void;
  /** Hidden for good, until the rep shows it again (D72, spec §5A). */
  hide: () => void;
  copyAddress: () => void;
}

export const notFitSheet = (s: Signal, on: NotFitActions): Modal => {
  const choice = (label: string, act: () => void) =>
    h(
      'button',
      {
        type: 'button',
        class: 'notfit__item',
        onClick: () => {
          sheet.close();
          act();
        },
      },
      label,
    );
  const sheet = modal(
    s.business_name,
    h(
      'div',
      { class: 'notfit' },
      choice('Not a fit: a chain', on.chain),
      choice('Not a fit: not my kind of business', on.kind),
      choice('Not a fit: outside my area', on.area),
      choice('Later (30 days)', on.later),
      choice('Hide', on.hide),
      h(
        'a',
        {
          class: 'notfit__item',
          href: signalSource(s).url,
          target: '_blank',
          rel: 'noopener',
          onClick: () => sheet.close(),
        },
        'Open the record',
      ),
      choice('Copy address', on.copyAddress),
    ),
  );
  return sheet;
};

/** The sheet for one card, wired to the inbox, open: a choice that takes the card away runs `before`
 *  first (the desk moves its selection on). Replaces the previous sheet. */
export const openNotFitSheet = (
  inbox: Pick<Triage, 'dismiss' | 'later' | 'hide'>,
  s: Signal,
  previous: Modal | null,
  before?: () => void,
): Modal => {
  previous?.remove();
  const gone = (act: () => Promise<void>) => () => {
    before?.();
    void act();
  };
  const sheet = notFitSheet(s, {
    chain: gone(() => inbox.dismiss(s, 'chain')),
    kind: gone(() => inbox.dismiss(s, 'kind')),
    area: gone(() => inbox.dismiss(s, 'area')),
    later: gone(() => inbox.later(s)),
    hide: gone(() => inbox.hide(s)),
    copyAddress: () =>
      void copyText(signalAddress(s) ?? s.business_name).then((ok) =>
        shell.toast(ok ? 'Address copied' : 'Could not copy the address', ok ? undefined : { kind: 'error' }),
      ),
  });
  document.body.append(sheet.el);
  sheet.open();
  return sheet;
};
